/* Cross-project saved views — the handful of questions a studio asks daily,
   answered across every project at once. Pure derivation; no new state. */

import type { Project } from "./types";

export interface ViewRow { project: string; pid: string; ref: string; title: string; meta: string; section: string; urgent?: boolean }
export interface SavedView { key: string; label: string; blurb: string; rows: (projects: Project[], me: string) => ViewRow[] }

const today = () => new Date().toISOString().slice(0, 10);
const dayDiff = (d: string) => Math.round((+new Date(d) - Date.now()) / 86_400_000);

export const SAVED_VIEWS: SavedView[] = [
  {
    key: "rfis", label: "Open RFIs", blurb: "Everything waiting on the studio or a consultant",
    rows: (ps) => ps.flatMap((p) => (p.rfis ?? []).filter((r) => r.status !== "closed").map((r) => ({
      project: p.name, pid: p.id, ref: r.ref, title: r.subject,
      meta: `ball: ${r.ballInCourt}${r.dueOn ? ` · due ${r.dueOn}` : ""}`, section: "rfis",
      urgent: r.ballInCourt === "studio" || (!!r.dueOn && r.dueOn < today()),
    }))),
  },
  {
    key: "overdue", label: "Overdue money", blurb: "Invoices past their due date, everywhere",
    rows: (ps) => ps.flatMap((p) => (p.invoices ?? []).filter((i) => (i.status === "due" || i.status === "overdue") && i.dueOn < today()).map((i) => ({
      project: p.name, pid: p.id, ref: i.ref, title: i.title,
      meta: `${i.amount} ${p.currency} · ${-dayDiff(i.dueOn)}d overdue`, section: "billing", urgent: true,
    }))),
  },
  {
    key: "awaiting", label: "Awaiting the client", blurb: "Approvals, variations and briefs sitting with them",
    rows: (ps) => ps.flatMap((p) => [
      ...(p.approvals ?? []).filter((a) => a.status === "issued").map((a) => ({ project: p.name, pid: p.id, ref: a.ref, title: a.title, meta: "approval unsigned", section: "drawings" })),
      ...(p.variations ?? []).filter((v) => v.status === "issued").map((v) => ({ project: p.name, pid: p.id, ref: v.ref, title: v.title, meta: `${v.amount} awaiting decision`, section: "billing" })),
      ...(p.brief?.status === "sent" ? [{ project: p.name, pid: p.id, ref: "BRIEF", title: "Design brief with the client", meta: "not returned", section: "brief" }] : []),
    ]),
  },
  {
    key: "myreviews", label: "Change requests to answer", blurb: "Client remarks the studio hasn't responded to",
    rows: (ps) => ps.flatMap((p) => (p.reviews ?? []).flatMap((r) => r.sheets.flatMap((s) => s.remarks
      .filter((m) => m.author === "client" && !m.response)
      .map((m) => ({ project: p.name, pid: p.id, ref: s.code, title: m.body.slice(0, 90), meta: `${r.title} · ${m.authorName}`, section: "review", urgent: true }))))),
  },
  {
    key: "ift", label: "IFT sheets", blurb: "Everything issued for tender or construction",
    rows: (ps) => ps.flatMap((p) => (p.deliverables ?? []).filter((d) => d.phase === "IFT" && !d.archived && (d.previewUrl || d.fileUrl)).map((d) => ({
      project: p.name, pid: p.id, ref: d.code, title: d.title,
      meta: `${d.discipline}${d.revision ? ` · rev ${d.revision}` : ""}${d.building ? ` · ${d.building}` : ""}`, section: "drawings",
    }))),
  },
  {
    key: "snags", label: "Open snags", blurb: "Defects still to fix or accept",
    rows: (ps) => ps.flatMap((p) => (p.snags ?? []).filter((s) => s.status !== "accepted").map((s) => ({
      project: p.name, pid: p.id, ref: s.ref, title: s.title,
      meta: [s.location, s.trade, s.status].filter(Boolean).join(" · "), section: "snags", urgent: s.severity === "high",
    }))),
  },
];

/* ---- fee-erosion forecast -------------------------------------------------
   A project loses money quietly: the work advances faster than the fee is drawn,
   change requests pile up unbilled, variations sit undrawn. This reads the
   signals already in the record and says which projects to look at. */

export interface Erosion {
  pid: string; name: string; code: string;
  risk: "high" | "medium" | "low";
  score: number;                    // 0-100, higher is worse
  progressPct: number;              // how far the work has gone
  billedPct: number;                // how much of the fee has been invoiced
  gap: number;                      // progress − billed, in points
  reasons: string[];
  unbilledExtras: number;           // value of approved-but-unbilled variations
  flaggedExtras: number;            // scope flags never raised as a variation
  currency: string;
}

export function feeErosion(projects: Project[]): Erosion[] {
  const out: Erosion[] = [];
  for (const p of projects) {
    const b = p.billing;
    const stages = (p as any).stages ?? [];
    const done = stages.filter((s: any) => s.status === "done").length;
    const active = stages.findIndex((s: any) => s.status === "active");
    const progressPct = stages.length ? Math.round(((done + (active >= 0 ? 0.5 : 0)) / stages.length) * 100) : 0;
    const fee = b?.contractSum ?? 0;
    const invoiced = (p.invoices ?? []).reduce((s, i) => s + (i.net ?? i.amount), 0);
    const billedPct = fee > 0 ? Math.round((invoiced / fee) * 100) : 0;
    const gap = progressPct - billedPct;

    const unbilledExtras = (p.variations ?? []).filter((v) => v.status === "approved" && !v.invoiceId).reduce((s, v) => s + v.amount, 0);
    const flagged = (p.scopeFlags ?? []).filter((f) => !f.dismissed && f.verdict !== "in-scope" && !f.variationId);
    const flaggedValue = flagged.reduce((s, f) => s + (f.suggestedAmount ?? 0), 0);
    let openRemarks = 0;
    for (const r of (p.reviews ?? [])) for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client" && !m.response) openRemarks++;
    const revisionChurn = (p.deliverables ?? []).reduce((s, d) => s + Math.max(0, (d.issues?.length ?? 1) - 1), 0);
    const overdue = (p.invoices ?? []).filter((i) => (i.status === "due" || i.status === "overdue") && i.dueOn < today()).length;

    const reasons: string[] = [];
    let score = 0;
    if (fee > 0 && gap >= 15) { score += Math.min(40, gap); reasons.push(`Work is ${gap} points ahead of billing (${progressPct}% done, ${billedPct}% invoiced)`); }
    if (unbilledExtras > 0) { score += 20; reasons.push(`${unbilledExtras.toLocaleString()} of approved variations not yet invoiced`); }
    if (flagged.length) { score += Math.min(20, flagged.length * 7); reasons.push(`${flagged.length} flagged extra${flagged.length === 1 ? "" : "s"} never raised as a variation${flaggedValue ? ` (~${flaggedValue.toLocaleString()})` : ""}`); }
    if (revisionChurn >= 12) { score += 12; reasons.push(`${revisionChurn} sheet revisions — heavy rework`); }
    if (openRemarks >= 8) { score += 8; reasons.push(`${openRemarks} client change requests still open`); }
    if (overdue) { score += 10; reasons.push(`${overdue} invoice${overdue === 1 ? "" : "s"} overdue`); }
    if (fee === 0) reasons.push("No agreed fee recorded — nothing to measure against");

    score = Math.min(100, score);
    out.push({
      pid: p.id, name: p.name, code: p.code,
      risk: score >= 50 ? "high" : score >= 25 ? "medium" : "low",
      score, progressPct, billedPct, gap, reasons, unbilledExtras, flaggedExtras: flagged.length,
      currency: b?.currency ?? p.currency,
    });
  }
  return out.sort((a, b) => b.score - a.score);
}
